import {useMutation, useQuery, useQueryClient} from "react-query";
import type {DateRange} from "../../common/newDatePicker/datePickerShared";
import {
    type CrmDemographicsChartsV4,
    type CrmInsightsRequestV4,
    type CrmOverviewTimeSeriesV4,
    type CrmChannelsTimeSeriesV4,
    type CrmRatesTimeSeriesV4,
    type CrmSeasonalityV4,
    type CrmGuestValueV4,
    type InsightRankedGuestExclusionUpdateV4,
    type InsightRankedGuestExclusionV4,
    type InsightRankedGuestListV4,
    type InsightTileV4,
} from "../models/crm-v4";
import {CrmInsightsV4Service} from "../services/crm-insights-v4.service";

// local calendar date — toISOString() would shift the day backwards for any positive UTC offset
function toIsoDate(value: Date): string {
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");

    return `${value.getFullYear()}-${month}-${day}`;
}

export const INSIGHTS_V2_STATS_KEY = "crm-insights-v4-stats";
export const INSIGHTS_V2_TIME_SERIES_KEY = "crm-insights-v4-overview-timeseries";
const SEASONALITY_KEY = "crm-insights-v4-seasonality-by-year";
const CHANNEL_TIME_SERIES_KEY = "crm-insights-v4-channel-timeseries";
const RATE_TIME_SERIES_KEY = "crm-insights-v4-overview-rate-timeseries";
const DEMOGRAPHIC_CHARTS_KEY = "crm-insights-v4-demographic-charts";
const GUEST_VALUE_KEY = "crm-insights-v4-guest-value";
const GUEST_VALUE_EXCLUSIONS_KEY = "crm-insights-v4-guest-value-exclusions";

export function buildRequest(range: DateRange, compareRange?: DateRange | null): CrmInsightsRequestV4 {
    return {
        from: toIsoDate(range.start),
        to: toIsoDate(range.end),
        ...(compareRange
            ? {compareFrom: toIsoDate(compareRange.start), compareTo: toIsoDate(compareRange.end)}
            : {}),
    };
}

export function useInsightsV2OverviewStats(range: DateRange, compareRange?: DateRange | null) {
    const request = buildRequest(range, compareRange);

    return useQuery<InsightTileV4[]>({
        queryKey: [INSIGHTS_V2_STATS_KEY, "overview", request],
        queryFn: () => CrmInsightsV4Service.overview(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2DemographicsStats(range: DateRange, compareRange?: DateRange | null) {
    const request = buildRequest(range, compareRange);

    return useQuery<InsightTileV4[]>({
        queryKey: [INSIGHTS_V2_STATS_KEY, "demographics", request],
        queryFn: () => CrmInsightsV4Service.demographics(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2GuestValue(range: DateRange, compareRange?: DateRange | null) {
    const request = buildRequest(range, compareRange);

    return useQuery<CrmGuestValueV4>({
        queryKey: [GUEST_VALUE_KEY, request],
        queryFn: () => CrmInsightsV4Service.guestValue(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2GuestValueExclusions() {
    return useQuery<InsightRankedGuestExclusionV4[]>({
        queryKey: [GUEST_VALUE_EXCLUSIONS_KEY],
        queryFn: () => CrmInsightsV4Service.guestValueExclusions(),
        refetchOnWindowFocus: false,
    });
}

export function useExcludeInsightsV2GuestValueProfile() {
    const queryClient = useQueryClient();

    return useMutation(
        (update: InsightRankedGuestExclusionUpdateV4) => CrmInsightsV4Service.excludeGuest(update),
        {
            onSuccess: exclusions => {
                queryClient.setQueryData([GUEST_VALUE_EXCLUSIONS_KEY], exclusions);
                queryClient.invalidateQueries([GUEST_VALUE_KEY]);
            },
        },
    );
}

export function useIncludeInsightsV2GuestValueProfile() {
    const queryClient = useQueryClient();

    return useMutation(
        ({ranking, profileId}: {ranking: InsightRankedGuestListV4; profileId: string}) =>
            CrmInsightsV4Service.includeGuest(ranking, profileId),
        {
            onSuccess: exclusions => {
                queryClient.setQueryData([GUEST_VALUE_EXCLUSIONS_KEY], exclusions);
                queryClient.invalidateQueries([GUEST_VALUE_KEY]);
            },
        },
    );
}

export function useInsightsV2TimeSeries(range: DateRange, compareRange?: DateRange | null) {
    const request = buildRequest(range, compareRange);

    return useQuery<CrmOverviewTimeSeriesV4>({
        queryKey: [INSIGHTS_V2_TIME_SERIES_KEY, request],
        queryFn: () => CrmInsightsV4Service.overviewTimeSeries(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

// no page-range argument on purpose: seasonality has its own fixed year series, so it caches once per session
export function useInsightsV2Seasonality() {
    return useQuery<CrmSeasonalityV4>({
        queryKey: [SEASONALITY_KEY],
        queryFn: () => CrmInsightsV4Service.overviewSeasonality(),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2ChannelTimeSeries(range: DateRange) {
    const request = buildRequest(range);

    return useQuery<CrmChannelsTimeSeriesV4>({
        queryKey: [CHANNEL_TIME_SERIES_KEY, request],
        queryFn: () => CrmInsightsV4Service.overviewChannelsTimeSeries(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2RateTimeSeries(range: DateRange) {
    const request = buildRequest(range);

    return useQuery<CrmRatesTimeSeriesV4>({
        queryKey: [RATE_TIME_SERIES_KEY, request],
        queryFn: () => CrmInsightsV4Service.overviewRatesTimeSeries(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}

export function useInsightsV2DemographicCharts(range: DateRange, compareRange?: DateRange | null) {
    const request = buildRequest(range, compareRange);

    return useQuery<CrmDemographicsChartsV4>({
        queryKey: [DEMOGRAPHIC_CHARTS_KEY, request],
        queryFn: () => CrmInsightsV4Service.demographicCharts(request),
        keepPreviousData: true,
        refetchOnWindowFocus: false,
    });
}
